import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ConfirmationResultModal,
  Flex,
  FormField,
  Select,
} from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import type { AssigneeSearchResult } from '../../../types/permissions.types'
import { AccountOrGroupPicker } from '../../accountOrGroupPicker'
import type { AssignRoleModalProps } from './AssignRoleModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function AssignRoleModal({
  domainRoles,
  searchAssignees,
  assignRole,
  onAssigned,
  onClose,
}: AssignRoleModalProps) {
  const { t } = useTranslation()
  const [modalState, setModalState] = useState<ModalState>('confirm')
  const [selectedAssignee, setSelectedAssignee] =
    useState<AssigneeSearchResult | null>(null)
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [isAssigning, setIsAssigning] = useState(false)

  const roleOptions: SelectOption[] = useMemo(
    () => domainRoles.map((role) => ({ value: role.id, label: role.name })),
    [domainRoles]
  )

  const handleSubmit = async () => {
    if (!selectedAssignee || !selectedRoleId) return

    setIsAssigning(true)

    try {
      await assignRole({
        assigneeId: selectedAssignee.id,
        roleId: selectedRoleId,
      })
      onAssigned()
      setModalState('success')
    } catch {
      setModalState('failure')
    } finally {
      setIsAssigning(false)
    }
  }

  const handleClose = () => {
    if (!isAssigning) {
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      isOpen
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.assignRole.title')}
      primaryButtonText={t('modal.assignRole.button.assign')}
      loadingText={t('modal.assignRole.button.assigning')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleSubmit}
      onClose={handleClose}
      onFailureClose={() => setModalState('confirm')}
      disabled={!selectedAssignee || !selectedRoleId || isAssigning}
      loading={isAssigning}
      primaryBtnTestId="assign-role-submit-button"
      secondaryBtnTestId="assign-role-cancel-button"
      success={{
        title: t('modal.assignRole.success.title'),
        description: t('modal.assignRole.success.description'),
        button: t('button.close'),
      }}
      failure={{
        title: t('modal.assignRole.error.title'),
        description: t('modal.assignRole.error.description'),
        button: t('button.close'),
      }}
    >
      <Flex direction="column" gap="md">
        <AccountOrGroupPicker
          dataTestId="assign-role"
          searchAssignees={searchAssignees}
          value={selectedAssignee}
          onChange={setSelectedAssignee}
        />
        <FormField label={t('modal.assignRole.roleLabel')} necessity="required">
          <Select
            name="role"
            placeholder={t('modal.assignRole.rolePlaceholder')}
            options={roleOptions}
            onChange={(option: SelectOption) => setSelectedRoleId(option.value)}
            data-testid="assign-role-select"
          />
        </FormField>
      </Flex>
    </ConfirmationResultModal>
  )
}
